import useUserCartViews from 'hooks/UserCart/useUserCartViews'
import { getCartViewItems } from 'lib/userCart/multiCartViews'
import { useCallback, useMemo } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from '@xstate/react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import focusStyle from 'styles/utils/focusStyle'
import SharedCartsUserAvatarGroup from '../SharedCartsUserAvatarGroup'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import { isDefaultMultiCart, isSharedMultiCart } from 'lib/userCart/multiCartVisibility'
import { EmptyObject } from 'lib/object/objectUtils'

const StyledOptionContainer = styled(Clickable)`
  padding: ${rem(8)};
  width: 100%;

  ${mediaHoverable} {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  &.is-create {
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

type Props = {
  multiCartItemIdsToMove: Array<string>
  onCreateNewSharedCart: (step: 'cartSelection' | 'createSharedCart') => void
  onClose: () => void
}

function SharedCartsCartSelectionModalContent(props: Props) {
  const intl = useIntl()
  const { multiCartItemIdsToMove, onCreateNewSharedCart, onClose } = props

  // This modal is getting opened from Snackbar which is out of context of MachineContextProvider.
  // hence we use getCartMachineActor() to get the actor ref else MultiCartContext.useActorRef() is preferred.
  const onlyChangeCart = multiCartItemIdsToMove.length === 0

  const multiCartActorRef = getCartMachineActor()
  if (!multiCartActorRef) {
    throw new Error('Multi cart actor ref not found')
  }
  const cartItems = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const { views, selectedView, splitDefault } = useUserCartViews(multiCartActorRef, onlyChangeCart, true)
  const selectableMultiCarts = useMemo(() => views.filter(view => view.viewId !== selectedView?.viewId && (onlyChangeCart || view.view === 'cart')), [views, selectedView?.viewId, onlyChangeCart])

  const handleCreateNewSharedCart = useCallback(() => {
    onCreateNewSharedCart('createSharedCart')
  }, [onCreateNewSharedCart])

  const handleAddItemsToSharedCart = useCallback((cartId: string) => {
    multiCartActorRef.send({
      type: 'select and move items to target cart',
      targetCartId: cartId,
      multiCartItemIdsToMove: multiCartItemIdsToMove,
    })

    onClose()
  }, [multiCartActorRef, multiCartItemIdsToMove, onClose])

  const handleActiveCartChange = useCallback((cartId: string, view: App.MultiCartView) => {
    multiCartActorRef.send({
      type: 'set active cart',
      cartId,
      view,
    })
    onClose()
  }, [multiCartActorRef, onClose])

  const handleCartSelection = useCallback((event: MouseEvent<HTMLElement>) => {
    const selected = views.find(view => view.viewId === event.currentTarget.dataset.cartId)
    if (!selected) return
    const cartId = selected.cart.cartId

    if (onlyChangeCart) {
      handleActiveCartChange(cartId, selected.view)
      return
    }

    handleAddItemsToSharedCart(cartId)
  }, [handleActiveCartChange, handleAddItemsToSharedCart, onlyChangeCart, views])

  return (
    <>
      <ModalHeader title={onlyChangeCart ? intl.formatMessage({ id: 'zAQEcnjmBC8mJ9RvCHZO', defaultMessage: 'Change cart', description: 'Modal title when changing active cart in the shopping cart interface' }) : intl.formatMessage({ id: 'DjJeD8k1M1wQzzMgs1h4', defaultMessage: 'Add to cart', description: 'Modal title when adding items to a cart in the shopping cart interface' })} onCloseButtonClick={onClose} />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={20}>
            <BodyText variant="large">{onlyChangeCart ? intl.formatMessage({ id: 'gxx6pjOFK-tWN_QdwEFX', defaultMessage: 'Choose which cart you would like to view.', description: 'Description when changing active cart in the shopping cart interface' }) : intl.formatMessage({ id: 'XXCFeCTa63pXawOlGjUN', defaultMessage: 'Choose which cart you would like to add this item to.', description: 'Description when adding items to a cart in the shopping cart interface' })}</BodyText>
            <CSSBreakpoint min="tablet">
              {!onlyChangeCart && <StyledOptionContainer onClick={handleCreateNewSharedCart}>
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  <StyledIconContainer className="is-create">
                    <LinePlusIcon />
                  </StyledIconContainer>
                  <BodyText variant="large">{intl.formatMessage({ id: 'w8l5toRzrD8sClqRuemA', defaultMessage: 'Create new shared cart', description: 'Option to create a new shared cart in the shopping cart interface' })}</BodyText>
                </Group>
              </StyledOptionContainer>}
            </CSSBreakpoint>
            {selectableMultiCarts.map(cart => (
              <StyledOptionContainer key={cart.viewId} data-cart-id={cart.viewId} onClick={handleCartSelection}>
                <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <StyledIconContainer>
                      {isDefaultMultiCart(cart.cart) ? <LineUserIcon colour="light-foreground-alt" /> : <LineUsersAltIcon colour="light-foreground-alt" />}
                    </StyledIconContainer>
                    <div>
                      <BodyText variant="large">{isDefaultMultiCart(cart.cart) && cart.view === 'cart' ? intl.formatMessage({ id: 'fiXHKmV3LOBtLQRSQrAp', defaultMessage: 'My cart', description: 'Default label for the user shopping cart in the cruise cart or navigation area' }) : cart.name}</BodyText>
                      {cartItems.status === 'loaded' && <BodyText variant="medium" colour="light-foreground-alt">
                        {intl.formatMessage({ id: 'sZHi1uQxgx8BBKrNJqWx', defaultMessage: '{count, plural, one {# item added} other {# items added}}', description: 'Pluralised count of items added to the shopping cart displayed in the snackbar notification' }, { count: getCartViewItems(Object.values(cartItems.data[cart.cart.cartId] ?? EmptyObject), cart.cart, cart.view, splitDefault).filter(item => item.state !== 'deleted').length })}
                      </BodyText>}
                    </div>
                  </Group>
                  {isSharedMultiCart(cart.cart) && <SharedCartsUserAvatarGroup activeCart={cart.cart} />}
                </Group>
              </StyledOptionContainer>
            ))}
            <CSSBreakpoint max="mobile">
              {!onlyChangeCart && <StyledOptionContainer onClick={handleCreateNewSharedCart}>
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  <StyledIconContainer className="is-create">
                    <LinePlusIcon />
                  </StyledIconContainer>
                  <BodyText variant="large">{intl.formatMessage({ id: 'w8l5toRzrD8sClqRuemA', defaultMessage: 'Create new shared cart', description: 'Option to create a new shared cart in the shopping cart interface' })}</BodyText>
                </Group>
              </StyledOptionContainer>}
            </CSSBreakpoint>
          </Group>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SharedCartsCartSelectionModalContent
